import { useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function CheckerWorklist() {
  const [refresh, setRefresh] = useState(0);
  const [resOpen, setResOpen] = useState(false);
  const [sbOpen, setSbOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [newSerial, setNewSerial] = useState("");

  const openResolve = (r) => { setActive(r); setRemarks(""); setNewSerial(""); setResOpen(true); };
  const openSendBack = (r) => { setActive(r); setRemarks(""); setSbOpen(true); };

  const resolve = async () => {
    if (!remarks.trim()) { toast.error("Resolution remarks required"); return; }
    try {
      const body = { material_id: active.id, remarks };
      if (newSerial.trim()) body.new_serial = newSerial.trim();
      const { data } = await api.post("/checker/resolve", body);
      toast.success(`Resolved → ${data.new_status}`);
      setResOpen(false); setRefresh((n) => n + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Resolve failed"); }
  };
  const sendBack = async () => {
    if (!remarks.trim()) { toast.error("Remarks required"); return; }
    try {
      await api.post("/checker/send-back", { material_id: active.id, remarks });
      toast.success("Sent back to client");
      setSbOpen(false); setRefresh((n) => n + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const columns = [
    { key: "material_number", label: "Serial", mono: true, render: (r) => <span className="font-mono text-xs font-medium text-slate-900">{r.material_number}</span> },
    { key: "type_name", label: "Type", render: (r) => <Badge variant="secondary">{r.type_name}</Badge> },
    { key: "project_name", label: "Project" },
    { key: "pre_defect_status", label: "Prev. Status", render: (r) => <Badge className="bg-slate-100 text-slate-600">{r.pre_defect_status}</Badge>, hideMobile: true },
    { key: "defect_reason", label: "Reason", hideMobile: true },
    { key: "defect_remarks", label: "Remarks", hideMobile: true },
  ];

  return (
    <div>
      <PageHeader title="Checker Worklist" subtitle="Defective materials assigned to you — resolve or send back to client" />
      <DataTable
        testid="checker-table"
        columns={columns}
        searchPlaceholder="Search serial..."
        refreshKey={refresh}
        load={(params) => api.get("/checker/worklist", { params }).then((r) => r.data)}
        emptyText="No defective materials awaiting your action"
        rowActions={(r) => (
          <div className="flex justify-end gap-1">
            <Button data-testid={`resolve-btn-${r.id}`} variant="ghost" size="sm" onClick={() => openResolve(r)}><CheckCircle2 className="h-4 w-4 text-emerald-600" /></Button>
            <Button data-testid={`sendback-btn-${r.id}`} variant="ghost" size="sm" onClick={() => openSendBack(r)}><Send className="h-4 w-4 text-amber-600" /></Button>
          </div>
        )}
      />

      <Dialog open={resOpen} onOpenChange={setResOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Resolve · {active?.material_number}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">On resolve, material returns to <b>{active?.pre_defect_status}</b>.</p>
            <div><Label>Resolution Remarks *</Label><Input data-testid="resolve-remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1" /></div>
            <div><Label>Correct Serial Number (optional)</Label><Input data-testid="resolve-serial-input" value={newSerial} onChange={(e) => setNewSerial(e.target.value)} placeholder={active?.material_number} className="mt-1" /><p className="mt-1 text-xs text-slate-400">Old serial is retained in history; duplicates are blocked.</p></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setResOpen(false)}>Cancel</Button><Button data-testid="resolve-confirm-btn" onClick={resolve} className="bg-emerald-600 hover:bg-emerald-700">Mark Resolved</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sbOpen} onOpenChange={setSbOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Send Back to Client · {active?.material_number}</DialogTitle></DialogHeader>
          <div><Label>Remarks *</Label><Input data-testid="sendback-remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setSbOpen(false)}>Cancel</Button><Button data-testid="sendback-confirm-btn" onClick={sendBack} className="bg-amber-600 hover:bg-amber-700">Send Back</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
